import { ApiError, api } from "./api.js";
import { device, type Answers, type LocalAnswer } from "./device.js";

// Odpovědi sezení (REQ-021): každá se hned uloží v zařízení s rostoucím `client_seq`, na server
// jdou dávkou nejvýš po 5 s nebo 5 odpovědích. Bez připojení čekají a odejdou po připojení.

export const BATCH_SIZE = 5;
export const BATCH_MS = 5000;

export class ResponseSync {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private flushing: Promise<void> | null = null;
  private closed = false;
  private readonly onOnline = () => this.background();

  private constructor(
    private readonly id: string,
    private readonly token: string,
    private answers: Answers,
    private seq: number,
  ) {
    window.addEventListener("online", this.onOnline);
  }

  /** Odpovědi ze zařízení sloučené se stavem serveru; `client_seq` pokračuje od maxima (REQ-021). */
  static async open(id: string, token: string, server: { answered: readonly { itemId: string; value: readonly number[] | null; round: number }[]; maxClientSeq: number }): Promise<ResponseSync> {
    const local = await device.answers(id);
    for (const a of server.answered) {
      if (a.value === null) continue;
      const mine = local[a.itemId];
      if (mine === undefined || mine.synced) local[a.itemId] = { value: a.value, clientSeq: mine?.clientSeq ?? 0, durationMs: mine?.durationMs ?? null, round: a.round, synced: true };
    }
    const seq = Math.max(server.maxClientSeq, ...Object.values(local).map((a) => a.clientSeq));
    const sync = new ResponseSync(id, token, local, seq);
    if (sync.pending() > 0) sync.background();
    return sync;
  }

  close(): void {
    this.closed = true;
    window.removeEventListener("online", this.onOnline);
    if (this.timer !== null) clearTimeout(this.timer);
  }

  /** Odeslání na pozadí. Při neúspěchu zkusí znovu za 5 s nebo po připojení. */
  private background(): void {
    this.flush().catch(() => {
      if (!this.closed && this.timer === null) this.timer = setTimeout(() => this.background(), BATCH_MS);
    });
  }

  answer(itemId: string): LocalAnswer | undefined {
    return this.answers[itemId];
  }

  /** Všechny odpovědi v zařízení, i neodeslané (náhled bez připojení). */
  all(): Readonly<Answers> {
    return this.answers;
  }

  answeredIn(items: readonly string[]): number {
    return items.filter((i) => this.answers[i] !== undefined).length;
  }

  pending(): number {
    return Object.values(this.answers).filter((a) => !a.synced).length;
  }

  /** Uloží odpověď v zařízení a naplánuje odeslání. */
  async record(itemId: string, round: number, value: readonly number[], durationMs: number | null): Promise<void> {
    this.seq += 1;
    this.answers[itemId] = { value, clientSeq: this.seq, durationMs, round, synced: false };
    await device.saveAnswers(this.id, this.answers);
    if (this.pending() >= BATCH_SIZE) this.background();
    else this.timer ??= setTimeout(() => this.background(), BATCH_MS);
  }

  /** Odešle všechny neodeslané odpovědi. Bez připojení vyhodí `ApiError` s `offline`. */
  async flush(): Promise<void> {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    while (this.flushing !== null) await this.flushing.catch(() => undefined);
    const batch = Object.entries(this.answers).filter(([, a]) => !a.synced);
    if (batch.length === 0) return;
    this.flushing = (async () => {
      try {
        await api.saveResponses(
          this.id,
          this.token,
          batch.map(([itemId, a]) => ({ item_id: itemId, value: a.value, client_seq: a.clientSeq, duration_ms: a.durationMs })),
        );
      } catch (e) {
        // Odpověď do uzavřeného kola server odmítne (DES-001 §5): už nemá kam jít.
        if (!(e instanceof ApiError) || e.status !== 422) throw e;
      }
      for (const [itemId, a] of batch) if (this.answers[itemId]?.clientSeq === a.clientSeq) this.answers[itemId] = { ...a, synced: true };
      await device.saveAnswers(this.id, this.answers);
    })();
    try {
      await this.flushing;
    } finally {
      this.flushing = null;
    }
  }
}
